import { t } from "@lingui/macro";
import { useAtomValue } from "jotai";
import React, { useCallback } from "react";
import { KeyboardAvoidingView } from "react-native";

import { EventFormInputRenderer } from "@whoosh/design-system/src/components/controls/EventFormRenderer";
import { Avatar } from "@whoosh/design-system/src/components/display/Avatar";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { eventDetailFormConfigurationAtom } from "../../atoms/eventSignUpProcess";
import { useInitalizeFormResponses } from "../../hooks/useInitalizeFormResponses/useInitalizeFormResponses";
import { EventSignUpRegistration } from "../../types";

type CopyOverrides = {
  singular?: string | null;
  plural?: string | null;
};

type EventDetailSignUpFormResponsesProps = {
  registration: EventSignUpRegistration;
  isGroupEvent: boolean;
  transportationTypes: { id: string; code: string; customCode: string }[];
  handleFormChange: ({
    registration,
    change,
  }: {
    registration: EventSignUpRegistration;
    change: {
      id: string;
      value?: string;
      booleanValue?: boolean;
      transportationTypeId?: string;
    };
  }) => void;
  copyOverrides?: CopyOverrides;
};

const getRegistrantName = (
  registration: EventSignUpRegistration,
  copyOverrides?: CopyOverrides
) => {
  if (registration.type === "CUSTOMER" && !registration.registrant.playerId) {
    const playerNoun = getNounOverride(copyOverrides?.singular, t`Player`);

    return t`New ${playerNoun}`;
  }
  return `${registration.registrant.firstName} ${registration.registrant.lastName}`;
};

export const EventDetailSignUpFormResponses = ({
  registration,
  transportationTypes,
  handleFormChange,
  isGroupEvent,
  copyOverrides,
}: EventDetailSignUpFormResponsesProps) => {
  const eventFormQuestionConfigurations = useAtomValue(
    eventDetailFormConfigurationAtom
  );

  const handleChange = useCallback(
    ({
      id,
      value,
      booleanValue,
      transportationTypeId,
    }: {
      id: string;
      value?: string;
      booleanValue?: boolean;
      transportationTypeId?: string;
    }) => {
      handleFormChange({
        registration,
        change: { id, value, booleanValue, transportationTypeId },
      });
    },
    [handleFormChange, registration]
  );

  useInitalizeFormResponses({
    registration,
    handleChange,
    eventFormQuestionConfigurations: eventFormQuestionConfigurations ?? [],
  });

  const registrantName = getRegistrantName(registration, copyOverrides);

  const isCustomer = registration.type === "CUSTOMER";

  const isUnknownCustomer = isCustomer && !registration.registrant.playerId;

  const isGuest =
    registration.type === "GUEST" ||
    registration.type === "New Guest" ||
    isUnknownCustomer;

  const avatarType = isCustomer ? "customer" : isGuest ? "guest" : "member";
  return (
    <KeyboardAvoidingView>
      <Box pt={isGroupEvent ? 0 : 2}>
        {isGroupEvent ? (
          <HStack
            px={8}
            py={2}
            borderTopWidth={1}
            borderBottomWidth={1}
            borderColor="surface-dividers"
            mb={4}
            gap={4}
            alignItems="center"
          >
            <Avatar
              size="sm"
              type={avatarType}
              firstName={registration.registrant.firstName}
              lastName={registration.registrant.lastName}
              profileImagePublicId={
                registration.registrant.profileImage?.publicId
              }
            />
            <VStack>
              <Text variant="mainBaseMedium">{registrantName}</Text>
              {registration.unknownRegistrantDisplayName &&
              !registration.registrant.firstName ? (
                <Text color="text-secondary" variant="mainTiny">
                  {registration.unknownRegistrantDisplayName}
                </Text>
              ) : null}{" "}
            </VStack>
          </HStack>
        ) : null}

        {eventFormQuestionConfigurations?.map((f) => {
          const hideForm =
            (!isGuest && f.memberVisibility === "NONE") ||
            (isGuest && f.guestVisibility === "NONE");

          const required =
            (!isGuest && f.memberVisibility === "REQUIRED") ||
            (isGuest && f.guestVisibility === "REQUIRED");

          const formResponse = registration.formResponses?.find(
            (fr) => fr.questionConfigurationId === f.id
          );

          //TODO: EventFormInputRenderer should live in this features
          return hideForm ? null : (
            <Box px={6} key={f.id}>
              <EventFormInputRenderer
                {...f}
                id={f.id ?? ""}
                value={formResponse?.stringValue ?? ""}
                onChange={handleChange}
                booleanValue={formResponse?.booleanValue ?? undefined}
                required={required}
                transporationTypes={transportationTypes}
                transportationTypeId={
                  formResponse?.transportationTypeId ?? undefined
                }
                registrant={registration.registrant}
              />
            </Box>
          );
        })}
      </Box>
    </KeyboardAvoidingView>
  );
};
